// src/pages/Discovery.jsx
import WorkspaceInsights from '../components/brain/WorkspaceInsights';
import { PageHeader, StatGrid, Stat } from "../components/ui/page-header";
import { useLocation } from "react-router-dom";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Button } from "components/ui/button";
import { Plus, Search as SearchIcon } from "lucide-react";

import ProblemCardGrid from "components/discovery/ProblemCardGrid";
import CreateProblemDialog from "components/discovery/CreateProblemDialog";
import ConfidenceFilters from "components/discovery/ConfidenceFilters";
import CreateRoadmapItemDialog from "components/roadmap/CreateRoadmapItemDialog";
import { problemCardsService, evidenceService, roadmapItemsService } from "../services/database";
import { createInitiative, ensureInitiativeReminder } from "../services/productBrain";
import { useAuth } from "../contexts/AuthContext";

export default function Discovery() {
  const { user, loading } = useAuth();
  const focusProblemId = new URLSearchParams(useLocation().search).get("recordId");
  const [problems, setProblems] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Roadmap dialog state (prefilled from a problem)
  const [showRoadmapDialog, setShowRoadmapDialog] = useState(false);
  const [roadmapInitial, setRoadmapInitial] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    status: "all",
    severity: "all",
    confidence: "all",
    query: "",
    horizon: "all",
  });

  const loadData = useCallback(async () => {
    // Don't load if still authenticating or no user
    if (loading || !user?.id) {
      if (!loading && !user?.id) {
        // Auth completed but no user - clear data
        setProblems([]);
        setEvidence([]);
        setRoadmapItems([]);
        setIsLoading(false);
      }
      return;
    }

    setIsLoading(true);
    try {
      console.log('Discovery loading data for user:', user.id);

      const [p, e, r] = await Promise.all([
        problemCardsService.list("-updated_at", 999),
        evidenceService.list("-updated_at", 999),
        roadmapItemsService.list("-updated_at", 999),
      ]);

      console.log('Discovery data loaded:', {
        problems: p.length,
        evidence: e.length,
        roadmapItems: r.length
      });

      setProblems(p);
      setEvidence(e);
      setRoadmapItems(r);
    } catch (error) {
      console.error('Error loading Discovery data:', error);
      setProblems([]);
      setEvidence([]);
      setRoadmapItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, loading]);

  useEffect(() => {
    loadData();
    window.addEventListener('spcc:autopilot-updated', loadData);
    return () => window.removeEventListener('spcc:autopilot-updated', loadData);
  }, [loadData]);

  const handleCreateProblem = async (form) => {
    try {
      console.log('Creating problem card:', form);

      // Create the problem card using Supabase
      const newProblem = await problemCardsService.create({
        title: form?.title || "Untitled Problem",
        description: form?.description || "",
        severity: form?.severity || "medium",
        target_segment: form?.customer_segment || "",
        confidence_score: {
          desirability: Number(form?.desirability_score ?? 0),
          feasibility: Number(form?.feasibility_score ?? 0),
          viability: Number(form?.viability_score ?? 0)
        },
        status: form?.status || "discovered",
        tags: Array.isArray(form?.tags) ? form.tags : [],
        time_horizon: form?.time_horizon || "now",
      });

      console.log('Problem card created:', newProblem);

      // Create evidence if provided
      if (form?.evidence?.length && newProblem?.id) {
        for (const ev of form.evidence) {
          if (!ev.url) continue;
          try {
            await evidenceService.create({
              problem_id: newProblem.id,
              type: ev.type,
              url: ev.url,
              note: ev.note,
              dateISO: ev.dateISO,
            });
          } catch (error) {
            console.error('Error creating evidence:', error);
          }
        }
      }

      // Reload data to show the new problem
      await loadData();
      setShowCreateDialog(false);
    } catch (error) {
      console.error('Error creating problem card:', error);
      alert('Failed to create problem card. Please try again.');
    }
  };

  const filteredProblems = useMemo(() => {
    return problems.filter((problem) => {
      const statusMatch = filters.status === "all" || problem.status === filters.status;
      const severityMatch = filters.severity === "all" || problem.severity === filters.severity;

      let confidenceMatch = true;
      if (filters.confidence !== "all") {
        const confidence = problem.confidence_score || {};
        const d = Number(confidence.desirability || 0);
        const f = Number(confidence.feasibility || 0);
        const v = Number(confidence.viability || 0);
        const avg = (d + f + v) / 3;
        confidenceMatch =
          (filters.confidence === "low" && avg < 50) ||
          (filters.confidence === "medium" && avg >= 50 && avg < 80) ||
          (filters.confidence === "high" && avg >= 80) ||
          filters.confidence === "all";
      }

      const q = (filters.query || "").trim().toLowerCase();
      const queryMatch =
        q.length === 0 ||
        (problem.title || "").toLowerCase().includes(q) ||
        (problem.description || "").toLowerCase().includes(q) ||
        (Array.isArray(problem.tags) ? problem.tags.join(" ") : "").toLowerCase().includes(q) ||
        (problem.customer_segment || "").toLowerCase().includes(q) ||
        (problem.okr_ref || "").toLowerCase().includes(q);

      const horizonValue = problem.time_horizon || problem.horizon || "";
      const horizonMatch =
        filters.horizon === "all" ||
        (horizonValue && horizonValue.toLowerCase() === filters.horizon.toLowerCase());

      return statusMatch && severityMatch && confidenceMatch && queryMatch && horizonMatch;
    });
  }, [problems, filters]);

  // SINGLE avgConfidence memo — used in the stat card (no duplicate calc)
  const avgConfidence = useMemo(() => {
    if (problems.length === 0) return 0;
    const total = problems.reduce((sum, p) => {
      const confidence = p.confidence_score || {};
      const d = Number(confidence.desirability || 0);
      const f = Number(confidence.feasibility || 0);
      const v = Number(confidence.viability || 0);
      return sum + (d + f + v) / 3;
    }, 0);
    return Math.round(total / problems.length);
  }, [problems]);

  const openAddToRoadmap = (problem) => {
    setRoadmapInitial({
      id: crypto.randomUUID(),
      status: "draft",
      title: problem?.title || "",
      description: problem?.description || problem?.problem || "",
      problem_id: String(problem?.id || ""),
      okr_ref: problem?.okr_ref || "",
      time_horizon: problem?.time_horizon || problem?.horizon || "Next",
    });
    setShowRoadmapDialog(true);
  };

  const handleCreateRoadmap = async (payload) => {
    try {
      console.log('Creating roadmap item:', payload);
      const problem = problems.find(p => p.id === payload.problem_id);
      const item = await createInitiative(payload, problem);
      setShowRoadmapDialog(false);
      await loadData();
      await ensureInitiativeReminder(item);
    } catch (error) {
      console.error('Error creating roadmap item:', error);
      throw error;
    }
  };

  return (
    <div>
      <PageHeader
        icon={SearchIcon}
        eyebrow="Discover"
        title="Discovery Hub"
        description="Validate problems with evidence and link them to roadmap initiatives."
        actions={<>
          <input
            type="search"
            aria-label="Search problems"
            placeholder="Search title, tags, OKR…"
            value={filters.query}
            onChange={(e) => setFilters((f) => ({ ...f, query: e.target.value }))}
            style={{ width: 240 }}
          />
          <select
            aria-label="Time horizon filter"
            value={filters.horizon}
            onChange={(e) => setFilters((f) => ({ ...f, horizon: e.target.value }))}
            style={{ width: 140 }}
          >
            <option value="all">All horizons</option>
            <option value="Now">Now</option>
            <option value="Next">Next</option>
            <option value="Later">Later</option>
          </select>
          <Button onClick={() => setShowCreateDialog(true)} variant="primary" data-guide="problem-add">
            <Plus size={15} />
            New problem
          </Button>
        </>}
      />

      <StatGrid>
        <Stat label="Total problems" value={problems.length} />
        <Stat label="Validated" value={problems.filter((p) => p.status === "validated").length} />
        <Stat label="Evidence items" value={evidence.length} />
        <Stat label="Avg confidence" value={`${avgConfidence}%`} />
      </StatGrid>
      <WorkspaceInsights area="discovery"/>

      {/* Filters */}
      <div style={{ marginBottom: 12 }}>
        <ConfidenceFilters
          filters={filters}
          onFiltersChange={setFilters}
          problemCount={filteredProblems.length}
        />
      </div>

      {/* Problems Grid */}
      <ProblemCardGrid
        focusProblemId={focusProblemId}
        problems={filteredProblems}
        evidence={evidence}
        roadmapItems={roadmapItems}
        isLoading={isLoading}
        onProblemUpdate={loadData}
        onAddToRoadmap={openAddToRoadmap}
      />

      {/* Create Problem Dialog */}
      <CreateProblemDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSubmit={handleCreateProblem}
      />

      {/* Create Roadmap Item Dialog (prefilled) */}
      <CreateRoadmapItemDialog
        open={showRoadmapDialog}
        onClose={() => setShowRoadmapDialog(false)}
        onSubmit={handleCreateRoadmap}
        problems={problems}
        initial={roadmapInitial || undefined}
      />
    </div>
  );
}
